import { priceFormatter } from '@2wunder/klarx-tool'
import { Col, Row } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import KTable from 'app/components/Modules/Table/KTable'
import { ComposableTypeEnum } from 'app/constants/Enums'
import { RecordType, TableProps } from 'app/constants/GlobalTypes'
import useTranslation from 'app/lib/hooks/useTranslation'
import _ from 'lodash'
import React, { useMemo } from 'react'

type DeliveryItemTableProps = {
  deliveryPriceRate?: IDeliveryPriceRateType
} & Partial<TableProps<IPriceListDeliveryItem>>

const Table: React.FC<DeliveryItemTableProps> = ({
  dataSource: data,
  deliveryPriceRate = 'range',
  ...others
}) => {
  const { tRecordType, tCore } = useTranslation({
    recordType: RecordType.price_list_item,
    scope: 'table'
  })
  const isRangePrice = deliveryPriceRate === 'range'

  const dataSource = useMemo(
    () =>
      _.chain(data || [])
        .groupBy('composableType')
        .map((value, key) => ({
          composableType: key,
          priceListDeliveryItems: value
        }))
        .value(),
    [data]
  )

  const getColumns = (
    composableType: keyof typeof ComposableTypeEnum
  ): ColumnProps<IPriceListDeliveryItem>[] => [
    {
      title: tRecordType(
        { id: 'delivery_price_for' },
        {
          name: tRecordType({ id: composableType })
        }
      ),
      dataIndex: 'label',
      width: '50%',
      render: (value: string) => (value === 'all' ? tRecordType({ id: 'all_weights' }) : value)
    },
    ...(isRangePrice
      ? [
          {
            title: tCore({ id: 'min_price' }),
            dataIndex: 'discountedMinPrice',
            render: (value: number) => priceFormatter(value)
          }
        ]
      : []),
    {
      title: isRangePrice ? tCore({ id: 'max_price' }) : tCore({ id: 'flat_price' }),
      dataIndex: 'discountedMaxPrice',
      render: (value: number) => priceFormatter(value)
    }
  ]

  return (
    <Row gutter={[0, 24]}>
      {dataSource.map((data) => (
        <Col span={24} key={data.composableType}>
          <KTable<IPriceListDeliveryItem>
            dataSource={data.priceListDeliveryItems || []}
            recordType={RecordType.price_list_item}
            params={{}}
            pagination={false}
            columns={getColumns(data.composableType as keyof typeof ComposableTypeEnum)}
            {...others}
          />
        </Col>
      ))}
    </Row>
  )
}

export default Table
